// Client fetch layer. Every proxied provider targets the same relative base
// (/api/proxy/<key>/...) in all three runtimes, so the client is deployment-
// agnostic. `auto` tries the upstream directly first and falls back to the proxy
// on the CORS/network failure signature; `proxy` is forced for FEC (key stays
// server-side); `direct` is used only where CORS is known-permissive.

import { ProviderError } from '../types/providers.ts';
import type { Transport } from '../types/ui.ts';

export type ProviderKey = 'fec' | 'polymarket' | 'kalshi';

const PROXY_BASE =
  (import.meta.env.VITE_PROXY_BASE as string | undefined) ?? '/api/proxy';

const DIRECT_BASE: Record<ProviderKey, string> = {
  fec: 'https://api.open.fec.gov/v1',
  polymarket: 'https://gamma-api.polymarket.com',
  kalshi: 'https://external-api.kalshi.com/trade-api/v2',
};

export interface FetchOpts {
  providerKey: ProviderKey;
  /** Path + query after the base, e.g. "markets?slug=foo". */
  upstreamPath: string;
  transport: Transport;
  signal?: AbortSignal;
  timeoutMs?: number;
}

async function once(url: string, signal: AbortSignal | undefined, timeoutMs: number): Promise<Response> {
  const ctrl = new AbortController();
  let timedOut = false;
  const timer = setTimeout(() => {
    timedOut = true;
    ctrl.abort();
  }, timeoutMs);
  const onAbort = () => ctrl.abort();
  signal?.addEventListener('abort', onAbort);
  try {
    return await fetch(url, { signal: ctrl.signal, headers: { accept: 'application/json' } });
  } catch (e) {
    if (timedOut) throw new ProviderError('timeout', `Timed out after ${timeoutMs}ms`);
    throw e; // network/CORS TypeError, or external abort
  } finally {
    clearTimeout(timer);
    signal?.removeEventListener('abort', onAbort);
  }
}

export async function fetchJson<T>(opts: FetchOpts): Promise<T> {
  const { providerKey, upstreamPath, transport, signal, timeoutMs = 12_000 } = opts;
  const proxyUrl = `${PROXY_BASE}/${providerKey}/${upstreamPath}`;
  const directUrl = `${DIRECT_BASE[providerKey]}/${upstreamPath}`;
  const order =
    transport === 'proxy' ? [proxyUrl] : transport === 'direct' ? [directUrl] : [directUrl, proxyUrl];

  let lastErr: unknown;
  for (const url of order) {
    if (signal?.aborted) break;
    try {
      const res = await once(url, signal, timeoutMs);
      if (!res.ok) {
        lastErr = new ProviderError('unreachable', `HTTP ${res.status} from ${providerKey}`);
        continue; // try the next transport (auto: direct → proxy)
      }
      return (await res.json()) as T;
    } catch (e) {
      // External abort (unmount / superseded refresh): propagate quietly.
      if (signal?.aborted && !(e instanceof ProviderError)) throw e;
      lastErr = e;
    }
  }

  if (lastErr instanceof ProviderError) throw lastErr;
  throw new ProviderError('unreachable', (lastErr as Error)?.message ?? 'Network error');
}
